import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, Loader2, History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DateTimePicker, tomorrowIso } from './DateTimePicker';
import { bookingsApi } from '../../services/api';
import { formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';

export function EventEditDialog({ bookingId, event, onOpenChange, onSaved }) {
  const [timestamp, setTimestamp] = useState('');
  const [status, setStatus] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(function () {
    if (event) { setTimestamp(event.timestamp || ''); setStatus(event.status || ''); setLocation(event.location || ''); setDescription(event.description || ''); }
  }, [event]);

  async function save(e) {
    e.preventDefault();
    if (!timestamp) { toast.error('Pick the date and time of the event'); return; }
    setSaving(true);
    try {
      const res = await bookingsApi.updateEvent(bookingId, event.index, { timestamp: timestamp, status: status, location: location, description: description });
      toast.success('Event updated — ' + formatDateTime(res.data.event.timestamp));
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update the event');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={Boolean(event)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="event-edit-dialog">
        <DialogHeader><DialogTitle>Adjust event</DialogTitle></DialogHeader>
        {event && (
          <form onSubmit={save} className="space-y-5">
            <div className="p-3 bg-gray-50 rounded-sm text-sm text-gray-600">Recorded {formatDateTime(event.original_timestamp || event.timestamp)}{event.edited_at ? ' · last edited ' + formatDateTime(event.edited_at) + ' by ' + event.edited_by : ''}</div>
            <div className="space-y-2"><Label>Date and time of the event</Label><p className="text-xs text-gray-500 -mt-1">Any past date is allowed — use it to place the milestone when it actually happened.</p><DateTimePicker value={timestamp} onChange={setTimestamp} maxDate={tomorrowIso()} testId="event-datetime" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Milestone</Label><Input value={status} onChange={function (e) { setStatus(e.target.value); }} className="rounded-sm" data-testid="event-status-input" /></div>
              <div className="space-y-1.5"><Label>Location</Label><Input value={location} onChange={function (e) { setLocation(e.target.value); }} className="rounded-sm" data-testid="event-location-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Description (shown to the customer)</Label><Input value={description} onChange={function (e) { setDescription(e.target.value); }} className="rounded-sm" data-testid="event-description-input" /></div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded-sm" onClick={function () { onOpenChange(false); }} data-testid="event-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-sm bg-ink hover:bg-ink-800" data-testid="event-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save event'}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Bridge timeline: every recorded milestone with the ability to correct its date/time and wording.
export function EventTimeline({ bookingId, onChanged }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async function () {
    if (!bookingId) { setEvents([]); return; }
    setLoading(true);
    try { const r = await bookingsApi.events(bookingId); setEvents(r.data.events); } catch (e) { setEvents([]); } finally { setLoading(false); }
  }, [bookingId]);

  useEffect(function () { load(); }, [load]);

  const sorted = [...events].sort(function (a, b) { return new Date(b.timestamp) - new Date(a.timestamp); });
  return (
    <div className="bg-white rounded-sm shadow-card p-6" data-testid="event-timeline">
      <h3 className="font-medium text-ink flex items-center gap-2 mb-1"><History className="h-4 w-4 text-blue-600" /> Shipment timeline</h3>
      <p className="text-sm text-gray-600 mb-4">Correct the date and time of a recorded milestone if it was entered late or wrongly. Only standard container milestones are shown on the public tracking page — events tagged <span className="text-[11px] text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded-full">internal</span> stay in the Bridge.</p>
      {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-500" /></div> : sorted.length === 0 ? <p className="text-sm text-gray-500" data-testid="event-timeline-empty">No events recorded yet.</p> : (
        <ol className="divide-y divide-gray-100">
          {sorted.map(function (ev) {
            return (
              <li key={ev.index} className="py-3 flex items-start gap-3" data-testid={'event-row-' + ev.index}>
                <div className="w-36 shrink-0 sv-mono text-sm text-ink pt-0.5" data-testid={'event-time-' + ev.index}>{formatDateTime(ev.timestamp)}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-ink text-sm">{ev.public_label || ev.status}{ev.public === false && <span className="ml-2 text-[11px] font-normal text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded-full" title="Not shown on the public tracking page" data-testid={'event-internal-' + ev.index}>internal</span>}{ev.edited_at && <span className="ml-2 text-[11px] font-normal text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded-full" title={'Edited by ' + ev.edited_by}>edited</span>}{ev.backdated && <span className="ml-2 text-[11px] font-normal text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded-full" title={'Recorded ' + formatDateTime(ev.recorded_at)} data-testid={'event-backdated-' + ev.index}>backdated</span>}</p>
                  <p className="text-sm text-gray-600 truncate">{ev.location}{ev.description ? ' — ' + ev.description : ''}</p>
                </div>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-sm text-gray-500 hover:text-ink" title="Adjust date/time" onClick={function () { setEditing(ev); }} data-testid={'event-edit-' + ev.index}><Pencil className="h-4 w-4" /></Button>
              </li>
            );
          })}
        </ol>
      )}
      <EventEditDialog bookingId={bookingId} event={editing} onOpenChange={function (o) { if (!o) setEditing(null); }} onSaved={function () { load(); if (onChanged) onChanged(); }} />
    </div>
  );
}
